import React, { useState, useRef, useEffect } from 'react';
import {
  X, Upload, FileAudio, Sparkles, Music, Wand2, Search,
  Check, ChevronRight, ChevronLeft, BookOpen
} from 'lucide-react';
import { EARTH, TYPOGRAPHY, calculateDuration } from '../theme/constants.js';
import { analyzeLoop } from '../lib/AudioAnalyzer.js';
import { classifyGenre } from '../lib/GenreClassifier.js';
import { inferLoopRole } from '../lib/LoopRoleInference.js';
import { generateArrangement } from '../lib/ArrangementGenerator.js';
import { generateFullGuidance } from '../lib/ProductionGuidance.js';
import { findMatchingReferences, generateReferenceArrangement, findCuratedMatch, mapITunesGenre } from '../lib/ReferenceLibrary.js';

function LoopAnalyzerPanel({ isOpen, onClose, onGenerate }) {
  const [audioFile, setAudioFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [genreResult, setGenreResult] = useState(null);
  const [roleResult, setRoleResult] = useState(null);
  const [arrangementResult, setArrangementResult] = useState(null);
  const [guidanceResult, setGuidanceResult] = useState(null);
  const [wizardStep, setWizardStep] = useState(0); // 0=upload, 1=analysis, 2=arrangement, 3=guidance
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [matchingReferences, setMatchingReferences] = useState([]);
  const [selectedReference, setSelectedReference] = useState(null);
  const [trackSearchQuery, setTrackSearchQuery] = useState('');
  const [trackSearchResults, setTrackSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedSearchTrack, setSelectedSearchTrack] = useState(null);
  const searchTimeoutRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState('');
  const [analysisError, setAnalysisError] = useState(null);
  const fileInputRef = useRef(null);

  // Demo mode (?demo=true): auto-load the bundled demo loop when the panel
  // opens, so live demos and the recorded product video hit the analysis
  // pipeline with zero file-picker interaction. Real users are unaffected —
  // the param is our internal demo entry, and the loop only fetches on open.
  const demoLoadedRef = useRef(false);
  useEffect(() => {
    if (!isOpen || demoLoadedRef.current) return;
    const isDemo = new URLSearchParams(window.location.search).get('demo') === 'true';
    if (!isDemo) return;
    demoLoadedRef.current = true;
    (async () => {
      try {
        const resp = await fetch('/audio/demo-loop.mp3');
        if (!resp.ok) return; // asset missing — fall back to normal upload UI
        const blob = await resp.blob();
        const file = new File([blob], 'Liquid DnB Demo.mp3', { type: 'audio/mpeg' });
        await processAudioFile(file);
      } catch {
        // network hiccup — leave the normal upload UI in place
      }
    })();
  }, [isOpen]);

  // Handle file drop
  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('audio/')) {
      await processAudioFile(file);
    }
  };

  // Handle file input
  const handleFileInput = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await processAudioFile(file);
    }
  };

  // Process audio file through the full analysis pipeline
  const processAudioFile = async (file) => {
    setAudioFile(file);
    setAudioUrl(URL.createObjectURL(file));
    setIsAnalyzing(true);
    setAnalysisError(null);
    setAnalysisResult(null);
    setGenreResult(null);
    setRoleResult(null);
    setArrangementResult(null);
    setGuidanceResult(null);
    setWizardStep(0);

    try {
      setAnalysisProgress('Detecting BPM and key...');
      const analysis = await analyzeLoop(file);
      setAnalysisResult(analysis);

      setAnalysisProgress('Classifying genre...');
      const genre = classifyGenre(analysis);
      setGenreResult(genre);
      setSelectedGenre(genre.primary.name);

      setAnalysisProgress('Inferring loop role...');
      const role = inferLoopRole(analysis, genre);
      setRoleResult(role);

      // Find matching reference tracks
      const refs = findMatchingReferences(analysis, genre, { maxResults: 5 });
      setMatchingReferences(refs);
      setSelectedReference(null);

      setWizardStep(1); // Move to analysis results
    } catch (err) {
      console.error('Audio analysis error:', err.message);
      setAnalysisError(`Analysis failed: ${err.message}`);
      // Still show fallback so user can see something
      setAnalysisResult({
        fileName: file.name,
        duration: 0,
        bars: 8,
        bpm: 120,
        bpmConfidence: 0,
        key: 'Unknown',
        energy: { bass: 0.5, mid: 0.5, treble: 0.5, percussive: 0.5, melodic: 0.5, overall: 0.5, density: 'balanced' }
      });
      setWizardStep(1);
    }

    setIsAnalyzing(false);
    setAnalysisProgress('');
  };

  // Generate arrangement with selected genre
  const generateArrangementFromAnalysis = async () => {
    if (!analysisResult || !genreResult || !roleResult) return;

    setIsAnalyzing(true);
    setAnalysisProgress('Building arrangement...');

    try {
      const genreToUse = selectedGenre || genreResult.primary.name;
      const arrangement = generateArrangement(analysisResult, genreResult, roleResult, { genreOverride: genreToUse });
      setArrangementResult(arrangement);

      const guidance = generateFullGuidance(arrangement, { loopElements: roleResult.elements });
      setGuidanceResult(guidance);

      setWizardStep(2);
    } catch (err) {
      console.error('Arrangement generation error:', err);
    }

    setIsAnalyzing(false);
    setAnalysisProgress('');
  };

  // Generate arrangement from a selected reference track
  const generateArrangementFromReference = async (refId) => {
    if (!analysisResult || !roleResult) return;

    setIsAnalyzing(true);
    setAnalysisProgress('Building arrangement from reference...');

    try {
      const arrangement = generateReferenceArrangement(refId, analysisResult, roleResult);
      setArrangementResult(arrangement);

      const guidance = generateFullGuidance(arrangement, { loopElements: roleResult.elements });
      setGuidanceResult(guidance);

      setWizardStep(2);
    } catch (err) {
      console.error('Reference arrangement error:', err);
    }

    setIsAnalyzing(false);
    setAnalysisProgress('');
  };

  // Search for published tracks via iTunes API proxy
  const handleTrackSearch = (query) => {
    setTrackSearchQuery(query);
    setSelectedSearchTrack(null);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!query || query.trim().length < 2) {
      setTrackSearchResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search-tracks?q=${encodeURIComponent(query.trim())}&limit=8`);
        if (!res.ok) throw new Error('Search failed');
        const data = await res.json();
        setTrackSearchResults(data.results || []);
      } catch (err) {
        console.error('Track search error:', err);
        setTrackSearchResults([]);
      }
      setIsSearching(false);
    }, 400); // 400ms debounce
  };

  // Generate arrangement from a searched track (iTunes result)
  const generateArrangementFromSearchResult = async (track) => {
    if (!analysisResult || !roleResult) return;
    setSelectedSearchTrack(track);
    setIsAnalyzing(true);
    setAnalysisProgress(`Analyzing "${track.title}" structure...`);

    try {
      // Check if we have a curated match
      const curated = findCuratedMatch(track.artist, track.title);
      let arrangement;

      if (curated) {
        // Exact match in our library — use real section structure
        setAnalysisProgress(`Found exact structure for "${track.artist} — ${track.title}"`);
        arrangement = generateReferenceArrangement(curated.id, analysisResult, roleResult);
        arrangement.searchedTrack = { artist: track.artist, title: track.title, artwork: track.artwork, genre: track.genre, year: track.year };
        arrangement.curatedMatch = true;
      } else {
        // No curated match — generate from genre template
        const mappedGenre = mapITunesGenre(track.genre);
        setAnalysisProgress(`Building ${mappedGenre} arrangement inspired by "${track.artist}"...`);
        arrangement = generateArrangement(analysisResult, { primary: { name: mappedGenre } }, roleResult);
        arrangement.searchedTrack = { artist: track.artist, title: track.title, artwork: track.artwork, genre: track.genre, year: track.year };
        arrangement.templateName = `${mappedGenre} (inspired by ${track.artist})`;
        arrangement.curatedMatch = false;
      }

      setArrangementResult(arrangement);
      const guidance = generateFullGuidance(arrangement, { loopElements: roleResult.elements });
      setGuidanceResult(guidance);
      setWizardStep(2);
    } catch (err) {
      console.error('Search arrangement error:', err);
    }

    setIsAnalyzing(false);
    setAnalysisProgress('');
  };

  // Apply arrangement to canvas
  const handleApply = () => {
    if (!arrangementResult) return;

    // Convert to the format expected by handleAIGenerate
    const output = {
      genre: arrangementResult.genre,
      tempo: arrangementResult.bpm,
      energyCurve: 'dynamic',
      sections: arrangementResult.sections.map(s => s.label),
      bars: arrangementResult.sections.map(s => s.bars),
      totalBars: arrangementResult.totalBars,
      estimatedDuration: `${Math.floor(arrangementResult.totalDuration / 60)}:${String(Math.round(arrangementResult.totalDuration % 60)).padStart(2, '0')}`,
      // Attach rich data for canvas integration
      _richSections: arrangementResult.sections,
      _guidance: guidanceResult,
      _analysis: analysisResult,
      _genre: genreResult,
      _role: roleResult,
      _audioUrl: audioUrl,
    };

    onGenerate(output);
    onClose();
  };

  const clearAudio = () => {
    setAudioFile(null);
    setAudioUrl(null);
    setAnalysisResult(null);
    setGenreResult(null);
    setRoleResult(null);
    setArrangementResult(null);
    setGuidanceResult(null);
    setWizardStep(0);
    setSelectedGenre(null);
  };

  if (!isOpen) return null;

  const stepLabels = ['Upload', 'Analysis', 'Arrangement', 'Guidance'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(28, 25, 23, 0.4)' }}>
      <div className="w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col" style={{ backgroundColor: '#faf8f5', border: '1px solid rgba(214, 211, 209, 0.3)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: EARTH.stone[200], background: `linear-gradient(135deg, ${EARTH.sage[100]}, ${EARTH.forest[100]})`  }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: EARTH.sage[600] }}>
              <Upload className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="anton-header" style={{ color: EARTH.stone[800], fontFamily: TYPOGRAPHY.display, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '14px' }}>LOOP → FULL TRACK</h2>
              <p className="text-xs" style={{ color: EARTH.stone[500] }}>
                {wizardStep === 0 && 'Drop your loop — we\'ll analyze it and build your arrangement'}
                {wizardStep === 1 && 'Here\'s what we found in your loop'}
                {wizardStep === 2 && 'Your arrangement is ready'}
                {wizardStep === 3 && 'Production guidance for each section'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/50" style={{ color: EARTH.stone[500] }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step Indicators */}
        {wizardStep > 0 && (
          <div className="flex items-center gap-1 px-6 pt-4">
            {stepLabels.map((label, i) => (
              <React.Fragment key={i}>
                <button
                  onClick={() => i <= wizardStep && setWizardStep(i > 0 ? i : wizardStep)}
                  className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all"
                  style={{
                    backgroundColor: i === wizardStep ? EARTH.sage[100] : i < wizardStep ? EARTH.forest[50] : EARTH.stone[50],
                    color: i === wizardStep ? EARTH.sage[700] : i < wizardStep ? EARTH.forest[600] : EARTH.stone[400],
                    cursor: i <= wizardStep ? 'pointer' : 'default'
                  }}
                >
                  {i < wizardStep ? <Check className="w-3 h-3" /> : null}
                  {label}
                </button>
                {i < stepLabels.length - 1 && <ChevronRight className="w-3 h-3" style={{ color: EARTH.stone[300] }} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="p-6 space-y-5 overflow-y-auto flex-1">

          {/* STEP 0: Upload */}
          {wizardStep === 0 && !audioFile && (
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all duration-200"
              style={{
                borderColor: isDragOver ? EARTH.sage[500] : EARTH.stone[200],
                backgroundColor: isDragOver ? EARTH.sage[50] : EARTH.stone[50]
              }}
            >
              <input ref={fileInputRef} type="file" accept="audio/*" onChange={handleFileInput} className="hidden" />
              <FileAudio className="w-16 h-16 mx-auto mb-4" style={{ color: isDragOver ? EARTH.sage[500] : EARTH.stone[400] }} />
              <p className="font-medium text-lg" style={{ color: EARTH.stone[700] }}>
                {isDragOver ? 'Drop it!' : 'Drop your loop here'}
              </p>
              <p className="text-sm mt-2" style={{ color: EARTH.stone[500] }}>
                MP3, WAV, AIFF, OGG — any audio loop you've created
              </p>
              <p className="text-xs mt-4" style={{ color: EARTH.stone[400] }}>
                We'll detect BPM, key, energy profile, and recommend a genre-specific arrangement
              </p>
            </div>
          )}

          {/* Analyzing state */}
          {wizardStep === 0 && audioFile && isAnalyzing && (
            <div className="rounded-xl p-8 text-center" style={{ backgroundColor: EARTH.sage[50], border: `1px solid ${EARTH.sage[200]}` }}>
              <Sparkles className="w-10 h-10 mx-auto mb-4 animate-spin" style={{ color: EARTH.sage[500] }} />
              <p className="font-medium" style={{ color: EARTH.sage[800] }}>Analyzing your loop...</p>
              <p className="text-sm mt-2" style={{ color: EARTH.sage[600] }}>{analysisProgress}</p>
              <p className="text-xs mt-1" style={{ color: EARTH.stone[400] }}>{audioFile.name}</p>
            </div>
          )}

          {/* STEP 1: Analysis Results */}
          {wizardStep === 1 && analysisResult && (
            <>
              {/* Error banner */}
              {analysisError && (
                <div className="rounded-xl p-4 mb-3" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                  <p className="font-medium text-sm" style={{ color: '#991b1b' }}>⚠ Analysis Error</p>
                  <p className="text-xs mt-1" style={{ color: '#b91c1c' }}>{analysisError}</p>
                  <p className="text-xs mt-2" style={{ color: '#dc2626' }}>Showing fallback values. Open browser console (F12) for details.</p>
                </div>
              )}
              {/* Audio player + file info */}
              <div className="rounded-xl p-4" style={{ backgroundColor: EARTH.sage[50], border: `1px solid ${EARTH.sage[200]}` }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Music className="w-5 h-5" style={{ color: EARTH.sage[700] }} />
                    <div>
                      <p className="font-medium text-sm" style={{ color: EARTH.sage[800] }}>{audioFile?.name}</p>
                      <p className="text-xs" style={{ color: EARTH.sage[600] }}>{analysisResult.duration?.toFixed(1)}s • {analysisResult.bars} bars</p>
                    </div>
                  </div>
                  <button onClick={clearAudio} className="p-1 rounded text-xs" style={{ color: EARTH.stone[500] }}>
                    Change file
                  </button>
                </div>
                {audioUrl && <audio controls className="w-full h-10" src={audioUrl} />}
              </div>

              {/* Core metrics */}
              <div className="grid grid-cols-4 gap-3">
                <div className="p-3 rounded-lg text-center" style={{ backgroundColor: EARTH.terracotta[50] }}>
                  <p className="text-2xl font-bold" style={{ color: EARTH.terracotta[600] }}>{analysisResult.bpm}</p>
                  <p className="text-xs" style={{ color: EARTH.stone[500] }}>BPM</p>
                </div>
                <div className="p-3 rounded-lg text-center" style={{ backgroundColor: EARTH.sage[50] }}>
                  <p className="text-2xl font-bold" style={{ color: EARTH.sage[600] }}>{analysisResult.bars}</p>
                  <p className="text-xs" style={{ color: EARTH.stone[500] }}>Bars</p>
                </div>
                <div className="p-3 rounded-lg text-center" style={{ backgroundColor: EARTH.olive[50] }}>
                  <p className="text-lg font-bold" style={{ color: EARTH.olive[600] }}>{analysisResult.key || 'N/A'}</p>
                  <p className="text-xs" style={{ color: EARTH.stone[500] }}>Key</p>
                </div>
                <div className="p-3 rounded-lg text-center" style={{ backgroundColor: EARTH.forest[50] }}>
                  <p className="text-lg font-bold" style={{ color: EARTH.forest[600] }}>{Math.round((analysisResult.bpmConfidence || 0) * 100)}%</p>
                  <p className="text-xs" style={{ color: EARTH.stone[500] }}>Confidence</p>
                </div>
              </div>

              {/* Energy Profile */}
              {analysisResult.energy && (
                <div className="rounded-xl p-4" style={{ backgroundColor: EARTH.stone[50], border: `1px solid ${EARTH.stone[200]}` }}>
                  <p className="text-xs font-medium mb-3" style={{ color: EARTH.stone[600] }}>ENERGY PROFILE</p>
                  {['bass', 'mid', 'treble', 'percussive', 'melodic'].map(band => {
                    const val = analysisResult.energy[band] || 0;
                    const colors = { bass: EARTH.terracotta, mid: EARTH.sage, treble: EARTH.olive, percussive: EARTH.clay, melodic: EARTH.forest };
                    const c = colors[band] || EARTH.stone;
                    return (
                      <div key={band} className="flex items-center gap-3 mb-2">
                        <span className="text-xs w-20 text-right font-medium" style={{ color: EARTH.stone[600] }}>{band}</span>
                        <div className="flex-1 h-3 rounded-full overflow-hidden" style={{ backgroundColor: EARTH.stone[200] }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${val * 100}%`, backgroundColor: c[500] || c }} />
                        </div>
                        <span className="text-xs w-10" style={{ color: EARTH.stone[500] }}>{(val * 100).toFixed(0)}%</span>
                      </div>
                    );
                  })}
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs px-2 py-1 rounded font-medium" style={{ backgroundColor: EARTH.sage[100], color: EARTH.sage[700] }}>
                      {analysisResult.energy.density}
                    </span>
                    <span className="text-xs" style={{ color: EARTH.stone[500] }}>overall: {(analysisResult.energy.overall * 100).toFixed(0)}%</span>
                  </div>
                </div>
              )}

              {/* Genre Detection */}
              {genreResult && (
                <div className="rounded-xl p-4" style={{ backgroundColor: EARTH.forest[50], border: `1px solid ${EARTH.forest[200]}` }}>
                  <p className="text-xs font-medium mb-3" style={{ color: EARTH.forest[600] }}>DETECTED GENRE</p>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-lg font-bold" style={{ color: EARTH.forest[800] }}>{genreResult.primary.name}</span>
                    <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: EARTH.forest[200], color: EARTH.forest[700] }}>
                      {Math.round(genreResult.primary.confidence * 100)}% match
                    </span>
                  </div>
                  {genreResult.alternatives.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs" style={{ color: EARTH.stone[500] }}>Also similar to:</span>
                      {genreResult.alternatives.slice(0, 3).map((alt, i) => (
                        <button
                          key={i}
                          onClick={() => setSelectedGenre(alt.name)}
                          className="text-xs px-2 py-1 rounded transition-all"
                          style={{
                            backgroundColor: selectedGenre === alt.name ? EARTH.sage[200] : EARTH.stone[100],
                            color: selectedGenre === alt.name ? EARTH.sage[800] : EARTH.stone[600],
                            border: `1px solid ${selectedGenre === alt.name ? EARTH.sage[400] : EARTH.stone[200]}`
                          }}
                        >
                          {alt.name} ({Math.round(alt.confidence * 100)}%)
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Loop Role */}
              {roleResult && (
                <div className="rounded-xl p-4" style={{ backgroundColor: EARTH.olive[50], border: `1px solid ${EARTH.olive[200]}` }}>
                  <p className="text-xs font-medium mb-2" style={{ color: EARTH.olive[600] }}>THIS LOOP IS A</p>
                  <p className="font-bold" style={{ color: EARTH.olive[800] }}>{roleResult.roleLabel}</p>
                  <p className="text-xs mt-1" style={{ color: EARTH.olive[600] }}>{roleResult.roleDescription}</p>
                  {roleResult.elements.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {roleResult.elements.map((el, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: EARTH.olive[200], color: EARTH.olive[800] }}>{el}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* TWO PATHS: Generic template OR Reference track */}
              <div className="rounded-xl p-4" style={{ backgroundColor: EARTH.stone[50], border: `1px solid ${EARTH.stone[200]}` }}>
                <p className="text-xs font-medium mb-3" style={{ color: EARTH.stone[500], textTransform: 'uppercase', letterSpacing: '0.5px' }}>How do you want to build your arrangement?</p>

                {/* Option 1: Generic template */}
                <button
                  onClick={generateArrangementFromAnalysis}
                  disabled={isAnalyzing}
                  className="w-full py-3 rounded-xl font-medium flex items-center justify-center gap-3 transition-all hover:scale-[1.01] neo-shadow mb-3"
                  style={{ backgroundColor: EARTH.sage[600], color: '#fff', fontFamily: TYPOGRAPHY.display, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '13px' }}
                >
                  {isAnalyzing && !selectedReference ? (
                    <><Sparkles className="w-4 h-4 animate-spin" /> {analysisProgress || 'Building...'}</>
                  ) : (
                    <><Wand2 className="w-4 h-4" /> BUILD {selectedGenre || genreResult?.primary?.name || ''} ARRANGEMENT</>
                  )}
                </button>

                {/* Divider */}
                <div className="flex items-center gap-3 my-4">
                  <div className="flex-1 h-px" style={{ backgroundColor: EARTH.stone[300] }} />
                  <span className="text-xs font-semibold" style={{ color: EARTH.terracotta[500], textTransform: 'uppercase', letterSpacing: '1px' }}>or mimic a track you love</span>
                  <div className="flex-1 h-px" style={{ backgroundColor: EARTH.stone[300] }} />
                </div>

                {/* Track Search */}
                <div className="relative">
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl" style={{ backgroundColor: '#fff', border: `2px solid ${trackSearchQuery ? EARTH.terracotta[300] : EARTH.stone[200]}`, transition: 'border-color 0.2s' }}>
                    <Search className="w-4 h-4 flex-shrink-0" style={{ color: EARTH.stone[400] }} />
                    <input
                      type="text"
                      value={trackSearchQuery}
                      onChange={(e) => handleTrackSearch(e.target.value)}
                      placeholder="Search any song — e.g. 'Sub Focus Rock It'"
                      className="flex-1 text-sm outline-none bg-transparent"
                      style={{ color: EARTH.stone[800] }}
                    />
                    {isSearching && <Sparkles className="w-3 h-3 animate-spin flex-shrink-0" style={{ color: EARTH.terracotta[400] }} />}
                    {trackSearchQuery && !isSearching && (
                      <button onClick={() => { setTrackSearchQuery(''); setTrackSearchResults([]); setSelectedSearchTrack(null); }} className="p-0.5 rounded-full hover:opacity-70">
                        <X className="w-3 h-3" style={{ color: EARTH.stone[400] }} />
                      </button>
                    )}
                  </div>

                  {/* Search Results */}
                  {trackSearchResults.length > 0 && (
                    <div className="mt-2 space-y-1.5 max-h-64 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                      {trackSearchResults.map((track) => {
                        const isSelected = selectedSearchTrack?.id === track.id;
                        const hasCurated = findCuratedMatch(track.artist, track.title);
                        return (
                          <button
                            key={track.id}
                            onClick={() => generateArrangementFromSearchResult(track)}
                            disabled={isAnalyzing}
                            className="w-full text-left rounded-lg p-2.5 transition-all hover:scale-[1.005]"
                            style={{
                              backgroundColor: isSelected ? EARTH.terracotta[50] : '#fff',
                              border: `1px solid ${isSelected ? EARTH.terracotta[300] : EARTH.stone[200]}`,
                            }}
                          >
                            <div className="flex items-center gap-3">
                              {/* Album artwork */}
                              {track.artwork ? (
                                <img src={track.artwork} alt="" className="w-10 h-10 rounded-md object-cover flex-shrink-0" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }} />
                              ) : (
                                <div className="w-10 h-10 rounded-md flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: EARTH.stone[200] }}>
                                  <Music className="w-4 h-4" style={{ color: EARTH.stone[400] }} />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold truncate" style={{ color: EARTH.stone[800] }}>
                                  {track.title}
                                </p>
                                <p className="text-xs truncate" style={{ color: EARTH.stone[500] }}>
                                  {track.artist}{track.year ? ` (${track.year})` : ''}
                                </p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: EARTH.stone[100], color: EARTH.stone[600], fontSize: '10px' }}>{track.genre}</span>
                                  {track.durationMs && (
                                    <span className="text-xs" style={{ color: EARTH.stone[400], fontSize: '10px' }}>
                                      {Math.floor(track.durationMs / 60000)}:{String(Math.round((track.durationMs % 60000) / 1000)).padStart(2, '0')}
                                    </span>
                                  )}
                                  {hasCurated && (
                                    <span className="text-xs px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: EARTH.terracotta[100], color: EARTH.terracotta[600], fontSize: '10px' }}>EXACT STRUCTURE</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {isAnalyzing && isSelected && (
                              <div className="flex items-center gap-2 mt-2 pl-13">
                                <Sparkles className="w-3 h-3 animate-spin" style={{ color: EARTH.terracotta[500] }} />
                                <span className="text-xs" style={{ color: EARTH.terracotta[500] }}>{analysisProgress || 'Building...'}</span>
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Empty state */}
                  {trackSearchQuery.length >= 2 && !isSearching && trackSearchResults.length === 0 && (
                    <p className="text-xs text-center py-3" style={{ color: EARTH.stone[400] }}>
                      No results found. Try a different search term.
                    </p>
                  )}

                  {/* Prompt */}
                  {!trackSearchQuery && (
                    <p className="text-xs text-center pt-2" style={{ color: EARTH.stone[400] }}>
                      Search millions of songs — we'll build an arrangement that mirrors their structure
                    </p>
                  )}
                </div>

                {/* Genre-matched suggestions from our curated library */}
                {matchingReferences.length > 0 && !trackSearchQuery && (
                  <div className="mt-4">
                    <p className="text-xs font-medium mb-2" style={{ color: EARTH.stone[500], textTransform: 'uppercase', letterSpacing: '0.5px' }}>Suggested matches from our library</p>
                    <div className="space-y-1.5">
                      {matchingReferences.slice(0, 3).map((ref) => (
                        <button
                          key={ref.id}
                          onClick={() => {
                            setSelectedReference(ref.id);
                            generateArrangementFromReference(ref.id);
                          }}
                          disabled={isAnalyzing}
                          className="w-full text-left rounded-lg p-2.5 transition-all hover:scale-[1.005]"
                          style={{
                            backgroundColor: selectedReference === ref.id ? EARTH.terracotta[50] : 'rgba(0,0,0,0.02)',
                            border: `1px solid ${selectedReference === ref.id ? EARTH.terracotta[300] : EARTH.stone[200]}`,
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-semibold" style={{ color: EARTH.stone[800] }}>
                                {ref.artist} — <span style={{ fontStyle: 'italic' }}>{ref.title}</span>
                              </p>
                              <p className="text-xs mt-0.5" style={{ color: EARTH.stone[500] }}>
                                {ref.genre} • {ref.bpm} BPM • {ref.totalBars} bars
                              </p>
                            </div>
                            <span className="text-xs px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: EARTH.terracotta[100], color: EARTH.terracotta[600], fontSize: '10px' }}>EXACT</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* STEP 2: Arrangement Preview */}
          {wizardStep === 2 && arrangementResult && (
            <>
              {/* Arrangement header */}
              <div className="rounded-xl p-4" style={{ backgroundColor: EARTH.forest[50], border: `1px solid ${EARTH.forest[200]}` }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold" style={{ color: EARTH.forest[800] }}>{arrangementResult.templateName}</span>
                  <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: EARTH.forest[200], color: EARTH.forest[700] }}>
                    {arrangementResult.totalBars} bars • {Math.floor(arrangementResult.totalDuration / 60)}:{String(Math.round(arrangementResult.totalDuration % 60)).padStart(2, '0')}
                  </span>
                </div>
                <p className="text-xs" style={{ color: EARTH.forest[600] }}>{arrangementResult.templateDescription}</p>
              </div>

              {/* Reference track insights (shown when using reference matching) */}
              {arrangementResult.referenceTrack && (
                <div className="rounded-xl p-4" style={{ backgroundColor: EARTH.terracotta[50], border: `1px solid ${EARTH.terracotta[200]}` }}>
                  <p className="text-xs font-medium mb-2" style={{ color: EARTH.terracotta[600], textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Producer Insights — {arrangementResult.referenceTrack.artist}
                  </p>
                  <div className="space-y-1.5">
                    {arrangementResult.referenceTrack.insights.map((insight, i) => (
                      <p key={i} className="text-xs" style={{ color: EARTH.terracotta[700], lineHeight: '1.4' }}>
                        • {insight}
                      </p>
                    ))}
                  </div>
                  <p className="text-xs mt-3" style={{ color: EARTH.terracotta[400] }}>
                    Original: {arrangementResult.referenceTrack.originalBpm} BPM in {arrangementResult.referenceTrack.originalKey} — adapted to your {arrangementResult.bpm} BPM loop
                  </p>
                </div>
              )}

              {/* Section list with energy bars */}
              <div className="space-y-2">
                {arrangementResult.sections.map((s, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg transition-all" style={{
                    backgroundColor: s.isLoopSection ? EARTH.sage[50] : EARTH.stone[50],
                    border: `1px solid ${s.isLoopSection ? EARTH.sage[200] : EARTH.stone[200]}`
                  }}>
                    <div className="w-12 text-center">
                      <span className="text-xs font-medium" style={{ color: EARTH.stone[500] }}>Bar {s.startBar}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium" style={{ color: s.isLoopSection ? EARTH.sage[800] : EARTH.stone[700] }}>
                          {s.label}
                        </span>
                        {s.isLoopSection && (
                          <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: EARTH.sage[200], color: EARTH.sage[700] }}>YOUR LOOP</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: EARTH.stone[200] }}>
                          <div className="h-full rounded-full" style={{
                            width: `${s.energy * 100}%`,
                            backgroundColor: s.energy >= 0.8 ? EARTH.terracotta[500] : s.energy >= 0.5 ? EARTH.sage[500] : EARTH.forest[400]
                          }} />
                        </div>
                        <span className="text-xs" style={{ color: EARTH.stone[400] }}>{s.bars}b</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* View guidance button */}
              {guidanceResult && (
                <button
                  onClick={() => setWizardStep(3)}
                  className="w-full py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all"
                  style={{ backgroundColor: EARTH.olive[100], color: EARTH.olive[700], border: `1px solid ${EARTH.olive[300]}` }}
                >
                  <BookOpen className="w-4 h-4" />
                  View production guidance for each section
                </button>
              )}
            </>
          )}

          {/* STEP 3: Production Guidance */}
          {wizardStep === 3 && guidanceResult && (
            <div className="space-y-4">
              <button
                onClick={() => setWizardStep(2)}
                className="flex items-center gap-1 text-xs font-medium"
                style={{ color: EARTH.sage[600] }}
              >
                <ChevronLeft className="w-3 h-3" /> Back to arrangement
              </button>
              {guidanceResult.map((section, i) => (
                <div key={i} className="rounded-xl p-4" style={{
                  backgroundColor: section.isLoopSection ? EARTH.sage[50] : EARTH.stone[50],
                  border: `1px solid ${section.isLoopSection ? EARTH.sage[200] : EARTH.stone[200]}`
                }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm" style={{ color: EARTH.stone[800] }}>{section.sectionLabel}</span>
                    <span className="text-xs" style={{ color: EARTH.stone[500] }}>{section.bars} bars • Bar {section.startBar}</span>
                  </div>

                  {/* Mix tips */}
                  {section.guidance.mixTips.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-medium mb-1" style={{ color: EARTH.sage[600] }}>MIX TIPS</p>
                      {section.guidance.mixTips.map((tip, j) => (
                        <p key={j} className="text-xs mb-1" style={{ color: EARTH.stone[600] }}>• {tip}</p>
                      ))}
                    </div>
                  )}

                  {/* Transition */}
                  <div className="mb-3">
                    <p className="text-xs font-medium mb-1" style={{ color: EARTH.terracotta[600] }}>TRANSITION IN</p>
                    <p className="text-xs" style={{ color: EARTH.stone[600] }}>{section.guidance.transitionTip}</p>
                  </div>

                  {/* DAW actions */}
                  {section.guidance.dawActions.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-medium mb-1" style={{ color: EARTH.olive[600] }}>DAW ACTIONS</p>
                      {section.guidance.dawActions.map((action, j) => (
                        <p key={j} className="text-xs mb-1" style={{ color: EARTH.stone[600] }}>→ {action}</p>
                      ))}
                    </div>
                  )}

                  {/* Energy tip */}
                  <p className="text-xs italic" style={{ color: EARTH.stone[500] }}>{section.guidance.energyTip}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between gap-3 p-4 border-t" style={{ borderColor: EARTH.stone[200] }}>
          <div className="flex gap-2">
            {wizardStep > 1 && (
              <button onClick={() => setWizardStep(wizardStep - 1)} className="px-4 py-2 rounded-lg text-sm" style={{ color: EARTH.stone[600], border: `1px solid ${EARTH.stone[200]}` }}>
                Back
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-6 py-2 rounded-lg" style={{ color: EARTH.stone[600] }}>Cancel</button>
            {wizardStep >= 2 && (
              <button
                onClick={handleApply}
                className="px-6 py-2 rounded-lg font-medium neo-shadow"
                style={{ backgroundColor: EARTH.forest[600], color: '#fff', fontFamily: TYPOGRAPHY.display, letterSpacing: '0.5px', textTransform: 'uppercase' }}
              >
                APPLY TO CANVAS
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export { LoopAnalyzerPanel };
